import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ShimmerContentBlock } from "react-shimmer-effects";
import { fetchUpcomingAuctionsApi } from "@/api/upcomingAuctions";
import UpcomingAuctionCountDown from "./UpcomingAuctionCountdown";
import { trackButtonClick } from "@/utils/analytics";
import { getPropertyURL } from "@/utils/propertyNavigation";

const UpcomingAuctionsTable = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [auctions, setAuctions] = useState([]);
  const navigate = useNavigate();

  const fetchUpcomingAuctions = async () => {
    try {
      const response = await fetchUpcomingAuctionsApi();
      setAuctions(response?.watchListResponse);
    } catch (error) {
      console.error("Error fetching upcoming auctions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingAuctions();
  }, []);

  const formatPrice = (price) => {
    if (price === 0) return "Free";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  const getButtonText = (cif, nda, listingStatus, propertyStatus, propertyCategory) => {
    if (propertyStatus === "Sale Pending" || propertyStatus === "Review") {
      return "Place Backup Bid";
    }
    if (cif === "Y" && nda === "Y" && propertyCategory === 'COMMERCIAL') {
      return listingStatus === "Active" && propertyStatus !== "PACT"
        ? "Bid Now"
        : "Confidentiality Agreement Signed";
    }else{
       if (cif === "Y" && nda === "Y" && propertyCategory === 'COMMERCIAL') {
      return listingStatus === "Active" && propertyStatus !== "PACT"
        ? "Bid Now"
        : "Registration Completed";
       }
    }
    if (cif === "Y" && nda === "N" && propertyCategory === 'COMMERCIAL') {
      return "Sign Confidentiality Agreement to Bid";
    }else if  (cif === "Y" && nda === "N" && propertyCategory !== 'COMMERCIAL')  {
       return listingStatus === "Active" && propertyStatus !== "PACT"
        ? "Bid Now"
        : "Registration Completed";
    }
    return "Register To Bid";
  };

  if (isLoading) {
    return (
      <div className="p-4">
        {[...Array(2)].map((_, index) => (
          <div key={index} className="mb-4 cfd-shimmer-wrapper">
            <ShimmerContentBlock
              title
              text
              cta
              // thumbnailWidth={400}
              // thumbnailHeight={420}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-0 mt-md-5 mt-xl-0">
      {auctions?.length > 0 && (
        <div className="d-none d-sm-block">
          <div className="row">
            <p className="col-10 px-4 upcoming-auctions-header mb-0">
              Property Details
            </p>
            <p className="col-2 upcoming-auctions-header mb-0">Actions</p>
          </div>
          <hr />
        </div>
      )}

      {auctions?.length > 0 ? (
        <>
          {auctions.map((data) => (
            <div
              className="col-12 auctions-card d-flex flex-column flex-md-row align-items-start"
              key={data?.propertyId}
            >
              <div className="col-12 col-md-2">
                <img
                  className="auction-image aution-image-tag"
                  src={
                    data?.propertyImageDetails?.imageStoragePath ||
                    "/assets/images/imageOnTheWay.webp"
                  }
                  alt="hubzu-image"
                  width={180}
                  height={120}
                  onClick={() => {
                    trackButtonClick({
                      eventName: "property_image_click",
                      eventCategory: "Watched-Properties",
                      eventSource: "on property image click",
                    });
                    navigate(getPropertyURL(`/property/${data?.propertyId}`, data?.propertyCategory, data?.propertyStatus));
                  }}
                />
              </div>
              <div className="col-12 col-md-4 align-items-start pl20">
                <a href={getPropertyURL(`/property/${data?.propertyId}`, data?.propertyCategory, data?.propertyStatus)}>
                  <p
                    className="auction-address pt-3 pt-sm-0 "
                    onClick={() => {
                      trackButtonClick({
                        eventName: "address_link_click",
                        eventCategory: "Watched-Properties",
                        eventSource: "on property address click",
                      });
                    }}
                  >
                    {data?.propertyAddress?.streetNumber}{" "}
                    {data?.propertyAddress?.streetName}{" "}
                    {data?.propertyAddress?.city},{" "}
                    {data?.propertyAddress?.state} {data?.propertyAddress?.zip}
                  </p>
                </a>

                <p className="aution-price">
                  {data?.listingDetail?.currentHighestPrice ? (
                    <>
                      Current Highest Bid:{"  "}
                      {formatPrice(data?.listingDetail?.currentHighestPrice)}
                    </>
                  ) : (
                    <>
                      Starting Bid:{" "}
                      {formatPrice(data?.listingDetail?.startingPrice)}
                    </>
                  )}
                </p>
              </div>
              <div className="col-12 col-md-3 align-items-start pl0 auction-timer  mx-auto mx-md-0">
                <p className="upcoming-status mt-2">
                  {data?.propertyStatus === "Sale Pending" ||
                  data?.propertyStatus === "Review"
                    ? "AUCTION ENDED"
                    : data?.propertyStatus === "Active"
                    ? "AUCTION ENDS IN"
                    : "AUCTION BEGINS IN"}
                </p>
                <p className="px-4 px-sm-0 px-lg-4 upcoming-timer">
                  {data?.listingDetail?.auctionTimeLeft > 0 && (
                    <UpcomingAuctionCountDown
                      until={data?.listingDetail?.auctionTimeLeft / 1000}
                    />
                  )}
                </p>
              </div>
              <div className="col-12 col-md-3 auction-actions text-center text-sm-end">
                <div className="w-200">
                  <button
                    className="btn p-2 btn-sm auction-thm"
                    onClick={(e) => {
                      const buttonText = getButtonText(
                        data?.cifCompleted,
                        data?.ndaAccepted,
                        data?.listingDetail?.listingStatus,
                        data?.propertyStatus,
                        data?.propertyCategory
                      );

                      const buttonActionMap = {
                        "Bid Now": "bid_now",
                        "Sign Confidentiality Agreement to Bid":
                        "sign_confidentiality_agreement",
                        "Register To Bid": "register_to_bid",
                      };

                      trackButtonClick({
                        eventName: "watched_property_bid_click",
                        eventCategory: "Watched-Properties",
                        eventSource: "on bid button click",
                        eventParams: {
                          button_action:
                            buttonActionMap[buttonText] || "view_details",
                        },
                      });
                      navigate(getPropertyURL(`/property/${data?.propertyId}`, data?.propertyCategory, data?.propertyStatus));
                    }}
                  >
                    {getButtonText(
                      data?.cifCompleted,
                      data?.ndaAccepted,
                      data?.listingDetail?.listingStatus,
                      data?.propertyStatus,
                      data?.propertyCategory
                    )}
                  </button>
                </div>

                {data?.cifId && (
                  <button
                    className="btn mt-3 btn-sm edit-cif-autions"
                    onClick={() => {
                      trackButtonClick({
                        eventName: "edit_cif_click",
                        eventCategory: "Watched-Properties",
                        eventSource: "on edit cif click",
                      });
                      window.open(`/ci/document/${data?.cifId}`);
                    }}
                  >
                    Edit contact information form
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="p-4 text-center">
          Properties you have shown interest in will be listed here.
        </div>
      )}
    </div>
  );
};

export default UpcomingAuctionsTable;
